
import React, { useState, useEffect } from 'react';
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, AreaChart, Area } from 'recharts';
import { UserRole } from '../types';
import { db, auth, enrollInClassByCode } from '../services/firebase';
import { collection, query, where, getDocs, onSnapshot, limit, doc, getDoc } from 'firebase/firestore';

const LOGO_URL = "https://i.ibb.co/NMthQdw/download-Photoroom.png";

const StatBox = ({ icon, label, val, color }: any) => (
    <div className="p-5 lg:p-6 rounded-2xl bg-white dark:bg-dark-card shadow-sm flex items-center gap-4 border border-gray-100 dark:border-gray-800 transition-theme">
        <div className={`w-12 h-12 rounded-xl ${color} flex-shrink-0 flex items-center justify-center text-white text-xl shadow-lg`}>
            <i className={`fas ${icon}`}></i>
        </div>
        <div className="min-w-0">
            <p className="text-xs text-gray-500 dark:text-gray-400 font-medium truncate uppercase tracking-wider">{label}</p>
            <h3 className="text-xl font-bold dark:text-white truncate">{val}</h3>
        </div>
    </div>
);

const Dashboard: React.FC<{ role: UserRole }> = ({ role }) => {
    const [stats, setStats] = useState({ students: 0, staff: 0, classes: 0, resources: 0 });
    const [recentNotes, setRecentNotes] = useState<any[]>([]);
    const [selectedNote, setSelectedNote] = useState<any>(null);
    const [studentClassName, setStudentClassName] = useState<string>('Unassigned');
    const [attendanceChartData, setAttendanceChartData] = useState<any[]>([]);
    const [isEnrollModalOpen, setIsEnrollModalOpen] = useState(false);
    const [classCode, setClassCode] = useState('');
    const [isEnrolling, setIsEnrolling] = useState(false);

    const handleEnroll = async (e: React.FormEvent) => {
        e.preventDefault();
        if (!auth.currentUser) return;
        setIsEnrolling(true);
        try {
            const className = await enrollInClassByCode(auth.currentUser.uid, classCode);
            setStudentClassName(className);
            setIsEnrollModalOpen(false);
            setClassCode('');
            alert(`Successfully enrolled in ${className}!`);
        } catch (err: any) {
            alert(err.message);
        }
        setIsEnrolling(false);
    };

    useEffect(() => {
        // Fetch Real Stats for Admin/Staff
        const fetchRealStats = async () => {
            const usersRef = collection(db, 'users');
            const classesRef = collection(db, 'classes');
            const resRef = collection(db, 'resources');
            const attRef = collection(db, 'attendance');

            const [studentSnap, staffSnap, classSnap, resSnap] = await Promise.all([
                getDocs(query(usersRef, where('role', '==', UserRole.STUDENT))),
                getDocs(query(usersRef, where('role', '==', UserRole.STAFF))),
                getDocs(classesRef),
                getDocs(resRef)
            ]);

            setStats({
                students: studentSnap.size,
                staff: staffSnap.size,
                classes: classSnap.size,
                resources: resSnap.size
            });

            // Aggregate real attendance data from last 5 days
            const last5Days = [...Array(5)].map((_, i) => {
                const d = new Date();
                d.setDate(d.getDate() - i);
                return d.toISOString().split('T')[0];
            }).reverse();

            const attSnap = await getDocs(attRef);
            const attDocs = attSnap.docs.map(d => d.data());
            
            const realChartData = last5Days.map(date => ({
                name: new Date(date).toLocaleDateString('en-US', { weekday: 'short' }),
                count: attDocs.filter(a => a.date === date).length,
                performance: Math.floor(Math.random() * 30) + 70 // Simulate performance trend
            }));
            setAttendanceChartData(realChartData);
        };

        if (role !== UserRole.STUDENT) {
            fetchRealStats();
        }

        if (role === UserRole.STUDENT && auth.currentUser) {
            const fetchStudentClass = async () => {
                const userDoc = await getDoc(doc(db, 'users', auth.currentUser!.uid));
                if (userDoc.exists() && userDoc.data().classId) {
                    const classDoc = await getDoc(doc(db, 'classes', userDoc.data().classId));
                    if (classDoc.exists()) {
                        setStudentClassName(classDoc.data().name);
                    }
                }
            };
            fetchStudentClass();
        }

        // Optimized Real-time listener for Notifications
        const q = query(collection(db, 'notifications'), limit(10));
        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map(d => ({ id: d.id, ...d.data() }));
            // Client-side sort to avoid index errors
            data.sort((a: any, b: any) => b.createdAt - a.createdAt);
            setRecentNotes(data);
        });

        return () => unsubscribe();
    }, [role]);

    return (
        <div className="space-y-6 lg:space-y-8 pb-10">
            <header className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                <div className="flex items-center gap-4">
                    <img src={LOGO_URL} className="w-12 h-12 object-contain" alt="BSD Logo" />
                    <div>
                        <h1 className="text-2xl font-black dark:text-white tracking-tight">
                            Portal Overview
                        </h1>
                        <p className="text-sm text-gray-500 uppercase tracking-widest text-[10px] font-bold">Active: {role}</p>
                    </div>
                </div>
                <div className="px-4 py-2 bg-green-100 dark:bg-green-900/20 text-green-700 dark:text-green-400 text-[10px] font-black rounded-full w-fit uppercase tracking-widest">
                    <i className="fas fa-circle-check mr-2"></i> Verified Session
                </div>
            </header>

            {role !== UserRole.STUDENT ? (
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 animate-fadeIn">
                    <StatBox icon="fa-users" label="Enrolled Students" val={stats.students} color="bg-gradient-to-br from-blue-500 to-indigo-600" />
                    <StatBox icon="fa-chalkboard-user" label="Active Staff" val={stats.staff} color="bg-gradient-to-br from-purple-500 to-pink-600" />
                    <StatBox icon="fa-school" label="Departments" val={stats.classes} color="bg-gradient-to-br from-orange-500 to-red-600" />
                    <StatBox icon="fa-cloud-arrow-up" label="Cloud Resources" val={stats.resources} color="bg-gradient-to-br from-teal-500 to-emerald-600" />
                </div>
            ) : (
                <div className="p-8 rounded-[2.5rem] bg-gradient-to-br from-purple-600 to-blue-600 text-white shadow-xl relative overflow-hidden group mb-4">
                    <div className="relative z-10">
                        <h3 className="text-2xl font-bold mb-2">My Academic Room</h3>
                        <p className="text-purple-100 mb-6 opacity-80 font-medium">
                            Current Assignment: <span className="underline decoration-2 underline-offset-4">{studentClassName}</span>
                        </p>
                        <div className="flex gap-4">
                            <button className="px-6 py-2 bg-white text-purple-600 rounded-xl font-bold text-sm shadow-lg hover:scale-105 transition-transform">View Subjects</button>
                            <button onClick={() => setIsEnrollModalOpen(true)} className="px-6 py-2 bg-white/10 text-white rounded-xl font-bold text-sm backdrop-blur-md border border-white/20 hover:bg-white/20 transition-all">
                                {studentClassName === 'Unassigned' ? 'Enroll in Class' : 'Change Class'}
                            </button>
                        </div>
                    </div>
                    <img src={LOGO_URL} className="absolute -bottom-8 -right-8 w-48 h-48 opacity-10 group-hover:rotate-12 transition-transform pointer-events-none" alt="Background Logo" />
                </div>
            )}

            {/* Enrollment Modal */}
            {isEnrollModalOpen && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md bg-black/40 animate-fadeIn">
                    <div className="bg-white dark:bg-dark-card w-full max-w-md rounded-[3rem] p-10 shadow-2xl relative border border-gray-100 dark:border-gray-800">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-purple-100 dark:bg-purple-900/30 text-purple-600 rounded-2xl flex items-center justify-center mx-auto mb-4 shadow-inner">
                                <i className="fas fa-key text-2xl"></i>
                            </div>
                            <h3 className="text-2xl font-black dark:text-white tracking-tight">Enter Class Code</h3>
                            <p className="text-gray-400 text-xs font-bold uppercase tracking-widest mt-1">4-Letter Security Key</p>
                        </div>

                        <form onSubmit={handleEnroll} className="space-y-6">
                            <div className="relative group">
                                <input 
                                    required
                                    maxLength={4}
                                    placeholder="e.g. ABCD"
                                    className="w-full bg-gray-50 dark:bg-gray-800/50 px-6 py-5 rounded-[1.5rem] dark:text-white outline-none border-2 border-transparent focus:border-purple-500 transition-all font-black text-center text-3xl tracking-[0.5em] uppercase"
                                    value={classCode}
                                    onChange={e => setClassCode(e.target.value.toUpperCase())}
                                />
                            </div>
                            
                            <div className="flex gap-3">
                                <button 
                                    type="button"
                                    onClick={() => setIsEnrollModalOpen(false)}
                                    className="flex-1 py-4 bg-gray-100 dark:bg-gray-800 text-gray-500 font-bold rounded-xl hover:bg-gray-200 transition-all"
                                >
                                    Cancel
                                </button>
                                <button 
                                    type="submit"
                                    disabled={isEnrolling || classCode.length !== 4}
                                    className="flex-[2] py-4 bg-purple-600 text-white font-bold rounded-xl shadow-lg hover:bg-purple-700 transition-all disabled:opacity-50 disabled:cursor-not-allowed"
                                >
                                    {isEnrolling ? 'Verifying...' : 'Join Room'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
                {/* Left/Middle Column (Charts or Secondary Stats) */}
                <div className="lg:col-span-2 space-y-6">
                    {role !== UserRole.STUDENT && attendanceChartData.length > 0 && (
                        <div className="bg-white dark:bg-dark-card p-6 rounded-[2rem] shadow-sm border border-gray-100 dark:border-gray-800">
                            <h3 className="text-[10px] font-black mb-6 dark:text-white uppercase tracking-widest flex items-center gap-2">
                                <i className="fas fa-chart-line text-purple-500"></i> Daily Check-in Volume
                            </h3>
                            <div className="h-[300px] w-full">
                                <ResponsiveContainer width="100%" height="100%">
                                    <AreaChart data={attendanceChartData}>
                                        <defs>
                                            <linearGradient id="colorAttend" x1="0" y1="0" x2="0" y2="1">
                                                <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.3}/>
                                                <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0}/>
                                            </linearGradient>
                                        </defs>
                                        <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#ccc" opacity={0.05} />
                                        <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#9ca3af', fontWeight: 600}} />
                                        <YAxis axisLine={false} tickLine={false} tick={{fontSize: 10, fill: '#9ca3af', fontWeight: 600}} />
                                        <Tooltip contentStyle={{ borderRadius: '16px', border: 'none', boxShadow: '0 10px 15px -3px rgba(0,0,0,0.1)', background: '#111', color: '#fff' }} />
                                        <Area type="monotone" dataKey="count" stroke="#8b5cf6" strokeWidth={3} fillOpacity={1} fill="url(#colorAttend)" />
                                    </AreaChart>
                                </ResponsiveContainer>
                            </div>
                        </div>
                    )}
                    
                    {role === UserRole.STUDENT && (
                        <div className="bg-white dark:bg-dark-card p-10 rounded-[3rem] border border-gray-100 dark:border-gray-800 shadow-sm flex items-center justify-between overflow-hidden relative group">
                            <div className="relative z-10">
                                <h3 className="text-xl font-black dark:text-white mb-2">Learning Resources</h3>
                                <p className="text-gray-400 text-sm font-medium mb-6">Explore PDFs, Videos and shared documents</p>
                                <button className="px-6 py-3 bg-purple-600 text-white rounded-xl font-bold text-xs shadow-lg hover:shadow-purple-200 transition-all">Go to Vault</button>
                            </div>
                            <i className="fas fa-laptop-code text-9xl absolute -right-4 -bottom-4 text-purple-500/5 group-hover:scale-110 transition-transform"></i>
                        </div>
                    )}
                </div>

                {/* Right Column (Live Broadcasts) */}
                <div className="lg:col-span-1">
                    <div className="bg-white dark:bg-dark-card p-6 rounded-[2rem] border border-gray-100 dark:border-gray-800 shadow-sm h-full flex flex-col">
                        <div className="flex items-center justify-between mb-6">
                            <div>
                                <h3 className="text-xl font-black dark:text-white">Announcements</h3>
                                <p className="text-[9px] text-gray-400 font-bold uppercase tracking-widest mt-0.5">Global Faculty Broadcasts</p>
                            </div>
                            <div className="w-10 h-10 rounded-xl bg-blue-50 dark:bg-blue-900/20 flex items-center justify-center text-blue-500">
                                <i className="fas fa-bullhorn text-sm"></i>
                            </div>
                        </div>
                        
                        <div className="space-y-4 flex-1 overflow-y-auto no-scrollbar pr-1">
                             {recentNotes.length > 0 ? recentNotes.map(note => (
                                <div 
                                    key={note.id} 
                                    onClick={() => setSelectedNote(note)}
                                    className="p-4 bg-gray-50/50 dark:bg-gray-800/40 rounded-2xl border border-gray-50 dark:border-gray-700/50 transition-all hover:bg-white dark:hover:bg-gray-800 hover:shadow-lg cursor-pointer group"
                                >
                                    <div className="flex justify-between items-start mb-2">
                                        <p className="text-[10px] font-black text-blue-500 uppercase tracking-widest">{note.senderName || 'Faculty'}</p>
                                        <p className="text-[8px] text-gray-400 font-bold">{new Date(note.createdAt).toLocaleDateString()}</p>
                                    </div>
                                    <h4 className="text-sm font-black dark:text-white mb-1 group-hover:text-blue-500 transition-colors truncate">{note.title}</h4>
                                    <p className="text-xs text-gray-500 dark:text-gray-400 line-clamp-1">{note.message}</p>
                                </div>
                             )) : (
                                <div className="h-full flex flex-col items-center justify-center opacity-20 py-10">
                                    <i className="fas fa-envelope-open-text text-5xl mb-4"></i>
                                    <p className="text-[10px] font-black uppercase tracking-[0.2em]">Quiet Session</p>
                                </div>
                             )}
                        </div>

                        <div className="mt-6 pt-6 border-t border-gray-50 dark:border-gray-800">
                            <p className="text-[9px] text-gray-400 font-black uppercase text-center tracking-widest">Always stay updated with portal news</p>
                        </div>
                    </div>
                </div>
            </div>

            {/* Announcement Reading Modal */}
            {selectedNote && (
                <div className="fixed inset-0 z-[100] flex items-center justify-center p-4 backdrop-blur-md bg-black/40 animate-fadeIn">
                    <div className="bg-white dark:bg-dark-card w-full max-w-lg rounded-[3rem] p-8 shadow-2xl relative border border-gray-100 dark:border-gray-800 overflow-hidden">
                        <div className="absolute top-0 right-0 p-8 opacity-5">
                            <i className="fas fa-bullhorn text-9xl"></i>
                        </div>
                        
                        <div className="relative z-10">
                            <div className="flex justify-between items-center mb-6">
                                <div className="flex items-center gap-3">
                                    <div className="w-12 h-12 rounded-2xl bg-blue-600 text-white flex items-center justify-center shadow-xl">
                                        <i className="fas fa-broadcast-tower"></i>
                                    </div>
                                    <div>
                                        <p className="text-[10px] font-black text-blue-600 uppercase tracking-[0.2em]">Faculty Notice</p>
                                        <h3 className="text-xl font-black dark:text-white tracking-tight">{selectedNote.title}</h3>
                                    </div>
                                </div>
                                <button 
                                    onClick={() => setSelectedNote(null)}
                                    className="w-10 h-10 rounded-xl bg-gray-100 dark:bg-gray-800 flex items-center justify-center hover:bg-red-500 hover:text-white transition-all"
                                >
                                    <i className="fas fa-times"></i>
                                </button>
                            </div>

                            <div className="bg-gray-50 dark:bg-gray-800/50 p-6 rounded-3xl mb-6">
                                <p className="text-sm leading-relaxed text-gray-600 dark:text-gray-300 whitespace-pre-wrap font-medium">
                                    {selectedNote.message}
                                </p>
                            </div>

                            <div className="flex items-center justify-between pt-6 border-t border-gray-100 dark:border-gray-800">
                                <div className="flex items-center gap-3">
                                    <div className="w-8 h-8 rounded-full bg-blue-100 dark:bg-blue-900/30 flex items-center justify-center text-blue-500 text-[10px]">
                                        <i className="fas fa-user-tie"></i>
                                    </div>
                                    <span className="text-[11px] font-black dark:text-white">{selectedNote.senderName}</span>
                                </div>
                                <span className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">
                                    {new Date(selectedNote.createdAt).toLocaleString([], { dateStyle: 'long', timeStyle: 'short' })}
                                </span>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default Dashboard;
